import { useCallback, useEffect, useMemo, useRef } from 'react'

type KeySequenceCallback = () => void

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

type UseKeySequenceOptions = {
  enabled?: boolean
}

export function useKeySequence(
  code: string[],
  onComplete: KeySequenceCallback,
  options: UseKeySequenceOptions = {},
) {
  const { enabled = true } = options

  const normalizedCode = useMemo(() => code.map((k) => k.toLowerCase()), [code])

  const indexRef = useRef(0) // next expected key index
  const lastMatchTimeRef = useRef<number | null>(null)
  const onCompleteRef = useRef(onComplete)

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  const reset = useCallback(() => {
    indexRef.current = 0
    lastMatchTimeRef.current = null
  }, [])

  const processInput = useCallback(
    (rawKey: string) => {
      const key = rawKey.toLowerCase()
      const now = Date.now()
      const index = indexRef.current

      // If we're in-progress, enforce timeout between steps
      if (index > 0 && lastMatchTimeRef.current !== null) {
        if (now - lastMatchTimeRef.current > 1000) {
          reset()
        }
      }

      const expectedKey = normalizedCode[indexRef.current]

      // "Avoid over-listening":
      // while idle (index 0), only start if first key matches.
      if (key !== expectedKey) {
        // Optional restart optimization:
        // if wrong key equals first key, start from step 1 immediately.
        if (key === normalizedCode[0]) {
          indexRef.current = 1
          lastMatchTimeRef.current = now
        } else {
          reset()
        }
        return
      }

      // Correct key
      indexRef.current += 1
      lastMatchTimeRef.current = now

      // Completed full sequence
      if (indexRef.current === normalizedCode.length) {
        onCompleteRef.current()
        reset()
      }
    },
    [normalizedCode, reset],
  )

  useEffect(() => {
    if (normalizedCode.length === 0 || !enabled) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) return

      processInput(event.key)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [normalizedCode, reset, enabled, processInput])

  return { processInput }
}
